import { Box, Button } from '@eatclubapp/ec-component-library';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { ReactComponent as IconChevronLeft } from '../../assets/icons/caret_left.svg';
import { ReactComponent as IconChevronRight } from '../../assets/icons/caret_right.svg';
import { Swipe } from '../Swipe';
import useStyles from './ImageCarouselStyles';

const ImageCarousel = ({
  showPagination = true,
  showControls = true, // If true, show the left/right arrows
  slides = [],
  hoverControls = false, // If true, controls only show when hovered with the mouse
  fitStyle = 'contain', // Contain or cover with the images
  showBackground = false, // Whether to have a blackish background behind the image
}) => {
  const classes = useStyles({ hoverControls, fitStyle, showBackground });

  const buttonStyles = {
    button: {
      width: '34px',
      height: '34px',
      borderRadius: '34px',
      // padding: '12px',
      backgroundColor: '#FFFFFF88',
      color: 'black',
    },
    buttonHover: {
      backgroundColor: '#FFFFFF',
      filter: 'none',
    },
  };

  const swipeRef = useRef(null);

  const [activeImage, setActiveImage] = useState(0);
  const previousActiveImage = useRef(0);
  const canShowControls = slides.length > 1;

  const showImage = (newIndex) => {
    previousActiveImage.current = activeImage;
    setActiveImage(newIndex);
  };

  const showPrevious = () => {
    previousActiveImage.current = activeImage;
    setActiveImage((activeImage - 1 + slides.length) % slides.length);
  };

  const showNext = () => {
    previousActiveImage.current = activeImage;
    setActiveImage((activeImage + 1) % slides.length);
  };

  /**
   * Better algorithm for images not being shown
   * If to the left of the current image, stack it immediately to the left
   * If to the right of the current image, stack it immediately to the right
   * Also loops well
   *
   * Return value is either -100 (percentage), 0, or 100, to feed into translateX
   */
  const getXPosition = (index) => {
    // If this is the active image, show it
    if (index === activeImage) {
      return 0;
    }

    // The slider gets weird with 1 or 2 images, so don't try to loop seamlessly
    if (slides.length <= 2) {
      return (index - activeImage) * 100;
    }

    // If looping Right
    if (index === (activeImage + 1) % slides.length) {
      return 100;
    }

    // If looping left
    if (index === (activeImage - 1 + slides.length) % slides.length) {
      return -100;
    }

    return (index - activeImage) * 100;
  };

  /**
   * Return whether this image is the current or previous one
   * Only slide the image if it's either the previous one sliding out or new one sliding in
   * This prevents them shuffling around in the background
   *
   * @param index
   * @returns {boolean}
   */
  const isAdjacentSlide = (index) => {
    // Note - uncomment the following line to see something funny
    // return false;

    return index === activeImage || index === previousActiveImage.current;
  };

  return (
    <Box className={classes.root} role='region'>
      <Box className={classes.carousel}>
        {canShowControls && showControls && (
          <>
            <Box className={classes.sliderButton} style={{ left: '16px' }}>
              <Button
                type='icon'
                style={buttonStyles}
                onClick={() => {
                  showPrevious();
                }}
              >
                <IconChevronLeft style={{ width: '8px' }} />
              </Button>
            </Box>

            <Box className={classes.sliderButton} style={{ right: '16px' }}>
              <Button
                type='icon'
                style={buttonStyles}
                onClick={() => {
                  showNext();
                }}
              >
                <IconChevronRight style={{ width: '8px' }} />
              </Button>
            </Box>
          </>
        )}

        {canShowControls && showPagination && (
          <Box className={classes.paginationContainer}>
            <Box className={classes.pagination}>
              {slides.map((slide, index) => (
                <Box
                  onClick={() => showImage(index)}
                  className={`${classes.paginationDot} ${
                    index === activeImage ? classes.paginationDotActive : ''
                  }`}
                />
              ))}
            </Box>
          </Box>
        )}

        <Swipe ref={swipeRef} onNext={showNext} onPrev={showPrevious}>
          {slides.map((slide, index) => (
            <Box
              key={slide.imageUrl}
              className={`${classes.slide} ${index === activeImage ? classes.slideActive : ''} ${
                isAdjacentSlide(index) ? classes.slideActiveRange : ''
              }`}
              role='tabpanel'
              style={{ transform: `translateX(${getXPosition(index)}%)` }}
            >
              <Box className={classes.slideContent}>
                {slide.caption && <Box className={classes.caption}>{slide.caption}</Box>}
                <img
                  className={classes.sliderImage}
                  src={slide.imageUrl}
                  alt={`Slider ${index + 1}`}
                  width='100%'
                  height={360}
                />
              </Box>
            </Box>
          ))}
        </Swipe>
      </Box>
    </Box>
  );
};

ImageCarousel.defaultProps = {
  showPagination: true,
  showControls: true,
  hoverControls: false,
  slides: [],
  fitStyle: 'contain',
  showBackground: false,
};

ImageCarousel.propTypes = {
  showPagination: PropTypes.bool,
  showControls: PropTypes.bool,
  hoverControls: PropTypes.bool,
  fitStyle: PropTypes.string,
  showBackground: PropTypes.bool,
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      caption: PropTypes.string,
      imageUrl: PropTypes.string,
    }),
  ),
};

export default ImageCarousel;
